<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'

import CameraIcon from '~/components/atoms/icons/CameraIcon.vue'
import LogoIcon from '~/components/atoms/icons/LogoIcon.vue'
import TeachmeCheckSymbol from '~/components/atoms/logos/TeachmeCheckSymbol.vue'
import BaseCluster from '~/components/foundations/BaseCluster.vue'
import TextBody from '~/components/foundations/TextBody.vue'
import OperatorSelectDialog from '~/components/molecules/OperatorSelectDialog.vue'
import EditOperatorDialog from '~/components/organisms/EditOperatorDialog.vue'
import TheMainMenu from '~/components/organisms/Header/TheMainMenu.vue'
import TodoBadge from '~/components/organisms/Header/TodoBadge.vue'
import QRCodeReaderDialog from '~/components/organisms/QRCodeReaderDialog.vue'
import { useAuth } from '~/composables/useAuth'
import { useCurrentOperator } from '~/composables/useCurrentOperator'
import { useFetchOperators } from '~/composables/useOperator'
import { useToast } from '~/composables/useToast'
import type { Operator } from '~/entities/operator'

const {
  currentOperator,
  showCamera = false,
  todoLink = undefined,
  todoCount = undefined,
} = defineProps<{
  currentOperator: Operator | null
  showCamera?: boolean
  todoLink?: RouteLocationRaw
  todoCount?: number
}>()

const { signOut } = useAuth()
const { t } = useI18n()
const { addToast } = useToast()
const route = useRoute()
const { currentOperator: operator } = useCurrentOperator()
const organizationId = Number(route.params.organizationId)

const showMenu = ref(false)

const switchOperator = async () => {
  showMenu.value = false
  showOperatorSelectDialog.value = true
}

const showEditOperatorDialog = ref(false)
const showOperatorSelectDialog = ref(false)
const showAddOperatorDialog = ref(false)
const { data: operators, refresh: refreshOperators } = await useFetchOperators()
const selectOperator = async (o: Operator) => {
  operator.value = o
  showOperatorSelectDialog.value = false
  addToast({
    message: t('作業者を切り替えました。'),
    color: 'success',
  })
}
const handleOperatorSelectDialogAdd = () => {
  showAddOperatorDialog.value = true
}
const doneCreateOperator = async () => {
  addToast({
    message: t('作業者を追加しました。'),
    color: 'success',
  })
  await refreshOperators()
}

const editProfile = () => {
  showEditOperatorDialog.value = true
  showMenu.value = false
}

const editProfileDone = (updatedOperator: Operator) => {
  operator.value = updatedOperator
  addToast({
    color: 'success',
    message: t('個人情報を更新しました。'),
  })
}

const logout = async () => {
  await signOut()
  await navigateTo({ name: 'login' })
}

const showQRCodeReaderDialog = ref(false)
const clickCameraIcon = () => {
  showQRCodeReaderDialog.value = true
}

const isTopPage = computed(() => {
  return (
    route.name === 'organizationId-checkFiles-select' || route.name === 'organizationId-tickets'
  )
})
</script>

<template>
  <div>
    <header class="header-root">
      <LogoIcon v-if="isTopPage" :height="20" />
      <NuxtLink
        v-else
        :to="{ name: 'organizationId-checkFiles-select', params: { organizationId } }"
      >
        <TeachmeCheckSymbol height="32px" width="32px" />
      </NuxtLink>
      <BaseCluster align="center" gap="m">
        <div class="icon-block">
          <button v-if="showCamera" class="action-button" @click="clickCameraIcon">
            <CameraIcon :size="24" />
          </button>
          <NuxtLink v-if="todoLink" :to="todoLink" class="action-button">
            <TodoBadge :badge-count="todoCount || 0" />
          </NuxtLink>
        </div>
        <div class="icon-block">
          <TheMainMenu v-model:is-active="showMenu" :current-operator="currentOperator">
            <div class="menu-block">
              <ul>
                <template v-if="currentOperator">
                  <li>
                    <TextBody size="large" class="menu-item name">{{
                      currentOperator.name
                    }}</TextBody>
                  </li>
                  <hr class="menu-divider" />
                  <li>
                    <button
                      v-t="'作業者の切替'"
                      class="action-button menu-item"
                      @click="switchOperator"
                    />
                  </li>
                  <hr class="menu-divider" />
                  <li>
                    <button v-t="'個人設定'" class="action-button menu-item" @click="editProfile" />
                  </li>
                </template>
                <hr class="menu-divider" />
                <li>
                  <button v-t="'ログアウト'" class="action-button menu-item" @click="logout" />
                </li>
              </ul>
            </div>
          </TheMainMenu>
        </div>
      </BaseCluster>
    </header>
    <QRCodeReaderDialog v-model:is-active="showQRCodeReaderDialog" />
    <EditOperatorDialog
      v-if="showEditOperatorDialog && currentOperator"
      v-model:is-active="showEditOperatorDialog"
      :operator="currentOperator"
      @done="editProfileDone"
    />
    <OperatorSelectDialog
      :is-active="showOperatorSelectDialog"
      :operators="operators || []"
      @select="selectOperator"
      @add="handleOperatorSelectDialogAdd"
      @close="showOperatorSelectDialog = false"
    />
    <EditOperatorDialog
      v-if="showAddOperatorDialog"
      v-model:is-active="showAddOperatorDialog"
      @done="doneCreateOperator"
      @error="(message) => addToast({ color: 'danger', message })"
    />
  </div>
</template>

<style scoped lang="scss">
.header-root {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 64px;
  box-shadow: 0px 2px 4px 0px rgba(55, 57, 56, 0.06);
  gap: 8px;
  padding: 12px 16px;
  background: white;
}

.icon-block {
  min-width: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.menu-block {
  width: 200px;
  ul {
    padding: 8px 0;
    margin: 0;
    list-style: none;
    li :deep(*) {
      @include textToken.body-large;
    }

    .name {
      color: var(--semantic-tokens-neutral-weak-text);
    }

    .menu-item {
      width: 100%;
      text-align: left;
      padding: 8px;
    }

    button.menu-item:hover {
      background: var(--semantic-tokens-neutral-xweak-surface);
    }
  }
  .menu-divider {
    margin: 8px 0;
  }
}

.action-button {
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  -webkit-tap-highlight-color: transparent;
}
</style>
